import { createStyles, WithStyles, withStyles } from "@material-ui/core/styles";
import React from "react";

import { RelodeTheme } from "../";
import Shape from "./shape";

interface ShapesProps extends WithStyles<typeof styles> {
    containingId: string;
    children?: any;
}

interface ShapesState {
    screen: any;
    context: any;
}

const styles = (theme: RelodeTheme) =>
    createStyles({
        root: {
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
        },
        shapes: {
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            zIndex: -1,
        },
    });

export const ShapesBackground = withStyles(styles, {
    name: "ShapesBackground",
})(
    class extends React.Component<ShapesProps, ShapesState> {
        public static defaultProps = {
            containingId: "shapes-background",
        };

        private contentRef: any;
        private shapes: any;
        private rafId: any;

        constructor(props: ShapesProps) {
            super(props);
            this.state = {
                screen: {
                    width: null,
                    height: null,
                },
                context: null,
            };
            this.contentRef = "";
            this.shapes = [];
        }
        componentDidMount() {
            window.addEventListener("resize", this.handleResize);
            this.setupCanvas();
        }
        componentDidUpdate() {
            this.updateCanvas();
        }
        componentWillUnmount() {
            window.removeEventListener("resize", this.handleResize);
        }
        setupCanvas() {
            const context = this.contentRef.getContext("2d");
            const { containingId } = this.props;

            this.setState(
                {
                    context,
                    screen: {
                        width: document.querySelector(`#${containingId}`)!.clientWidth,
                        height: document.querySelector(`#${containingId}`)!.clientHeight,
                    },
                },
                () => {
                    this.setupShapes();
                    this.updateCanvas();
                }
            );
        }
        setupShapes() {
            const CircleLarge = {
                lineWidth: "",
                lineColor: "",
                fillColor: "rgba(126,106,216,1)",
                startPosition: [-10, -30],
                start: [221, 441],
                bezier: [
                    [342, 441, 441, 342, 441, 221],
                    [441, 99, 342, 0, 221, 0],
                    [99, 0, 0, 99, 0, 221],
                    [0, 342, 99, 441, 221, 441],
                ],
            };
            const CircleSmall = {
                lineWidth: "",
                lineColor: "",
                fillColor: "rgba(92,79,176,1)",
                startPosition: [8, 50],
                start: [210, 250],
                bezier: [
                    [146, 291, 62, 272, 21, 208],
                    [-19, 144, -1, 60, 63, 19],
                    [126, -21, 210, -2, 251, 62],
                    [291, 125, 273, 210, 210, 250],
                ],
            };
            const SemiCylinder = {
                lineWidth: "",
                lineColor: "",
                fillColor: "rgba(63,48,163,1)",
                startPosition: [15, 55],
                start: [85, 370],
                bezier: [
                    [85, 370, 120, 320, 120, 320],
                    [56, 275, 41, 186, 87, 122],
                    [133, 57, 222, 41, 285, 86],
                    [285, 86, 321, 37, 321, 37],
                    [230, -28, 103, -5, 38, 87],
                    [-27, 179, -6, 306, 85, 370],
                    [85, 370, 85, 370, 85, 370],
                ],
            };
            const Triangle = {
                lineWidth: "",
                lineColor: "",
                fillColor: "rgba(92,79,176,1)",
                startPosition: [85, 20],
                start: [226, 169],
                bezier: [
                    [226, 169, 113, 193, 113, 193],
                    [113, 193, 0, 216, 0, 216],
                    [0, 216, 71, 0, 71, 0],
                    [71, 0, 226, 169, 226, 169],
                ],
            };
            const SemiCircle = {
                lineWidth: "",
                lineColor: "",
                fillColor: "rgba(63,48,163,1)",
                startPosition: [95, 30],
                start: [54, 192],
                bezier: [
                    [4, 165, -14, 103, 12, 54],
                    [38, 4, 100, -14, 149, 12],
                    [149, 12, 54, 192, 54, 192],
                    [54, 192, 54, 192, 54, 192],
                ],
            };
            const shapes = [CircleLarge, CircleSmall, SemiCylinder, Triangle, SemiCircle];
            this.shapes = shapes.map(
                shape =>
                    new Shape({
                        lineWidth: shape.lineWidth,
                        lineColor: shape.lineColor,
                        bezier: shape.bezier,
                        fillColor: shape.fillColor,
                        start: shape.start,
                        position: {
                            x: (this.state.screen.width / 100) * shape.startPosition[0],
                            y: (this.state.screen.height / 100) * shape.startPosition[1],
                        },
                    })
            );
        }

        handleResize = () => {
            const { containingId } = this.props;
            this.setState(
                {
                    screen: {
                        width:
                            typeof document !== "undefined" && document.querySelector(`#${containingId}`)!.clientWidth,
                        height:
                            typeof document !== "undefined" && document.querySelector(`#${containingId}`)!.clientHeight,
                    },
                },
                () => {
                    this.setupShapes();
                }
            );
        };
        updateCanvas = () => {
            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
            }
            this.state.context.save();
            this.state.context.fillStyle = "#21006e";
            this.state.context.fillRect(0, 0, this.state.screen.width, this.state.screen.height);
            this.shapes.map(shape => shape.render(this.state));
            this.state.context.restore();
            this.rafId = requestAnimationFrame(this.updateCanvas);
        };
        render() {
            const percentageHeight = (this.state.screen.height / 100) * 100;
            const { containingId, children, classes } = this.props;

            return (
                <div className={classes.root} id={containingId}>
                    <canvas
                        className={classes.shapes}
                        ref={contentRef => (this.contentRef = contentRef)}
                        width={this.state.screen.width}
                        height={this.state.screen.height}
                    />
                    {children}
                </div>
            );
        }
    }
);
